import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import moment from 'moment/moment';
import PropTypes from 'prop-types';
import {
    usePopupListeners,
    useUnscrollableBody,
    useWindowDimensions,
    useWindowDimensionsHeight
} from '../../../../../../common/utils/windowDimensions';
import UserContext, { UserProfileContext } from '../../../../../../common/utils/getContext';
import { existLocales } from '../../../../../../common/utils/dateLocales';
import T from '../../../../../../common/components/T';
import ReactDOM from 'react-dom';
import FormDate from '../../FormDate/FormDate';
import FormInput from '../../../form-input/FormInput/FormInput';
import { checkBrowser, checkSafari, getWithZoom, useWidgetTranslates } from '../../../../../../common/utils/utils';
import css from './CustomDatePicker.module.scss';
import classnames from 'classnames';
import { getFormattedDate } from '../../initData';
import SingleButton from '../../../../form-control-buttons/SingleButton/SingleButton';

const CustomDatePicker = ({
    handleClose,
    setVal,
    pickerElement,
    mobileRangeDateCustom,
    config = {},
    format_data = 'yyyy-MM-DD',
    options = {}
}) => {
    const execludeYear = !format_data || (!format_data?.includes('y') && !format_data?.includes('Y'));
    const { lang, time, settings } = useContext(UserContext);
    const { adminPage } = useContext(UserProfileContext);
    const [langLocale, setLangLocale] = useState(lang);
    const modalElement = useRef();
    useEffect(() => {
        if (existLocales[lang]) {
            registerLocale(lang, existLocales[lang]);
            setLangLocale(lang);
        } else {
            (async () => {
                const locale = await import('date-fns/locale/ru');
                const localeDef = await locale;
                if (localeDef.default) {
                    registerLocale('ru', localeDef.default);
                    setLangLocale('ru');
                }
            })();
        }
    }, [lang]);
    const date = time().format(format_data);

    const trans = useWidgetTranslates('datePickerRange');

    const maxDate = options?.max_date ? moment(options?.max_date, 'yyyy-MM-DD').toDate() : undefined;
    const initDateValue = options?.max_date
        ? moment(options?.max_date, 'yyyy-MM-DD').valueOf()
        : moment(date, format_data).valueOf();

    const [startDate, setStartDate] = useState(initDateValue);
    const [endDate, setEndDate] = useState(initDateValue);
    const handleChangeEndDate = (attr, date) => {
        setEndDate(date);
    };
    const handleChangeStartDate = (attr, date) => {
        setStartDate(date);
    };
    const isMobile = useWindowDimensions() < 768;
    const getInputValue = (data) => {
        if (Array.isArray(data)) {
            return { start: data[0], end: data[1] };
        }
        const datesArr = data.split('-');
        if (datesArr.length === 2) {
            return { start: datesArr[0], end: datesArr[1] };
        }
        return false;
    };
    const changeHandler = (v) => {
        const val = getInputValue(v);
        if (!val) return false;
        const { start, end } = val;
        setStartDate(start);
        setEndDate(end);
    };
    const dateFormat = useCallback(
        (dating) => {
            return getFormattedDate(dating, execludeYear, settings, lang);
        },
        [lang, execludeYear, settings]
    );
    const formatValue = (value) => {
        if (!value) return null;
        const toMoment = moment(value);
        return toMoment.isValid() ? dateFormat(toMoment) : null;
    };
    const heightDimensions = useWindowDimensionsHeight();
    const widthDimensions = useWindowDimensions();
    const mobile = widthDimensions < 768;
    const formatDate = (d) => {
        if (!d || !moment(d).isValid()) {
            return;
        }
        return moment(d).format(format_data);
    };
    const func = useCallback(() => {
        if (!mobile && modalElement.current && pickerElement.current) {
            const picker = pickerElement.current.getBoundingClientRect();
            const modal = modalElement.current.getBoundingClientRect();
            let lPos = config?.left ? picker.left : picker.right - modal.width;
            if (lPos < 0) {
                lPos = picker.left;
            }
            // TODO ZOOM

            if (getWithZoom(window?.innerWidth) - lPos <= 700) {
                lPos = (getWithZoom(window?.innerWidth) - 700) / 2;
            }

            let tPos = checkBrowser(picker.top) + checkSafari(window?.scrollY) + checkBrowser(picker.height) + 10;

            if (tPos + modal.height > checkSafari(window?.scrollY) + heightDimensions) {
                tPos = picker.top + checkSafari(window?.scrollY) - modal.height - 10;

                if (tPos < checkSafari(window?.scrollY)) {
                    tPos = checkSafari(window?.scrollY);
                }
            }

            modalElement.current.style.left = checkBrowser(lPos) + 'px';
            modalElement.current.style.top = tPos + 'px';
            modalElement.current.classList.add(css.visible);
            // TODO ZOOM
        } else if (mobile && modalElement.current) {
            modalElement.current.style.left = '0';
            modalElement.current.style.top = '0';
        }
    }, [mobile, pickerElement, heightDimensions, config]);

    usePopupListeners(pickerElement, func, modalElement, handleClose, isMobile);
    useUnscrollableBody(isMobile, 'PickerRange');

    return (
        <>
            {ReactDOM.createPortal(
                <>
                    <div
                        onClick={() => {
                            handleClose();
                        }}
                        className={css.customDatePickerBackdrop}
                    />
                    <div ref={modalElement} className={css.customDatePickerRange}>
                        <div className={css.containerDatePicker}>
                            <div className={css.mainContainer}>
                                <div className={css.horizontal}>
                                    {mobileRangeDateCustom && isMobile ? (
                                        <>
                                            <div onClick={() => handleClose()} className={css.closeModal}></div>
                                            <div className={css.title}>
                                                <p className={css.titleText}>
                                                    <T
                                                        textName='publication_date'
                                                        defaultStr='Дата публикации'
                                                        page='widget'
                                                        group='datePickerRange'
                                                        customTranslates={trans}
                                                        customLoading={!trans}
                                                    />
                                                </p>
                                            </div>
                                        </>
                                    ) : null}
                                    {mobileRangeDateCustom && isMobile ? null : (
                                        <div className={classnames(css.vertical, css.containerButton)}>
                                            <div
                                                onClick={() => {
                                                    handleClose();
                                                }}
                                                className={css.containerDatePickerDecor}
                                            />
                                            <div>
                                                {!mobileRangeDateCustom && mobile && (
                                                    <p className={css.customPickerTitle}>
                                                        <T
                                                            textName='shown-period'
                                                            defaultStr='Период отображения'
                                                            page='widget'
                                                            group='datePickerRange'
                                                            customTranslates={trans}
                                                            customLoading={!trans}
                                                        />
                                                    </p>
                                                )}
                                                <div
                                                    className={css.menuButton}
                                                    onClick={() => {
                                                        setStartDate(time().valueOf());
                                                        setEndDate(time().valueOf());
                                                    }}
                                                >
                                                    <T
                                                        textName='datePickerRangeToday-new'
                                                        defaultStr='Сегодня'
                                                        page='widget'
                                                        group='datePickerRange'
                                                        customTranslates={trans}
                                                        customLoading={!trans}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <div
                                                    className={css.menuButton}
                                                    onClick={() => {
                                                        const d = time().add(-1, 'days');
                                                        setStartDate(d.valueOf());
                                                        setEndDate(d.valueOf());
                                                    }}
                                                >
                                                    <T
                                                        textName='datePickerRangeYesterday-new'
                                                        defaultStr='Вчера'
                                                        page='widget'
                                                        group='datePickerRange'
                                                        customTranslates={trans}
                                                        customLoading={!trans}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <div
                                                    className={css.menuButton}
                                                    onClick={() => {
                                                        const d = time().add(-7, 'days');
                                                        setStartDate(d.valueOf());
                                                        setEndDate(time().valueOf());
                                                    }}
                                                >
                                                    <T
                                                        textName='datePickerRangeRange-new'
                                                        vars={{ days: '7' }}
                                                        defaultStr='За ${days} дней'
                                                        page='widget'
                                                        group='datePickerRange'
                                                        customTranslates={trans}
                                                        customLoading={!trans}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <div
                                                    className={css.menuButton}
                                                    onClick={() => {
                                                        const d = time().add(-30, 'days');
                                                        setStartDate(d.valueOf());
                                                        setEndDate(time().valueOf());
                                                    }}
                                                >
                                                    <T
                                                        textName='datePickerRangeRange-new'
                                                        vars={{ days: '30' }}
                                                        defaultStr='За ${days} дней'
                                                        page='widget'
                                                        group='datePickerRange'
                                                        customTranslates={trans}
                                                        customLoading={!trans}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <div
                                                    className={css.menuButton}
                                                    onClick={() => {
                                                        const d = time().subtract(1, 'months');
                                                        setStartDate(d.startOf('month').valueOf());
                                                        setEndDate(d.endOf('month').valueOf());
                                                    }}
                                                >
                                                    <T
                                                        textName='datePickerRangeMonth-new'
                                                        defaultStr='Прошлый месяц'
                                                        page='widget'
                                                        group='datePickerRange'
                                                        customTranslates={trans}
                                                        customLoading={!trans}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <div
                                                    className={css.menuButton}
                                                    onClick={() => {
                                                        setVal({
                                                            start: '',
                                                            end: ''
                                                        });
                                                        handleClose({
                                                            start: moment(new Date(0)).format(format_data),
                                                            end: time().format(format_data)
                                                        });
                                                    }}
                                                >
                                                    <T
                                                        textName='datePickerRangeAll'
                                                        defaultStr='За все время'
                                                        page='widget'
                                                        group='datePickerRange'
                                                        customTranslates={trans}
                                                        customLoading={!trans}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div
                                        className={classnames(
                                            css.containerCalendar,
                                            mobileRangeDateCustom && isMobile ? css.mobileCustomContainerCalendar : ''
                                        )}
                                    >
                                        {!mobileRangeDateCustom && isMobile && (
                                            <p className={css.mobileCustomDatePickerOwn}>
                                                <T
                                                    textName='choose-own'
                                                    defaultStr='Выбрать свой диапазон'
                                                    page='widget'
                                                    group='datePickerRange'
                                                    customTranslates={trans}
                                                    customLoading={!trans}
                                                />
                                                :
                                            </p>
                                        )}
                                        <div className={css.topContainer}>
                                            <div className={css.containerInput}>
                                                {isMobile ? (
                                                    <FormDate
                                                        execludeYear={execludeYear}
                                                        compStyle={{
                                                            wrapper: {
                                                                marginBottom: '0'
                                                            }
                                                        }}
                                                        value={startDate}
                                                        setFormData={handleChangeStartDate}
                                                        options={{
                                                            max_date: endDate
                                                        }}
                                                    />
                                                ) : (
                                                    <FormInput
                                                        compStyle={{
                                                            wrapper: {
                                                                marginBottom: '0'
                                                            }
                                                        }}
                                                        readonly
                                                        value={formatValue(startDate)}
                                                    />
                                                )}
                                            </div>
                                            <div className={css.textBetweenInput}>
                                                <label>-</label>
                                            </div>
                                            <div className={css.containerInput}>
                                                {isMobile ? (
                                                    <FormDate
                                                        execludeYear={execludeYear}
                                                        compStyle={{
                                                            wrapper: {
                                                                marginBottom: '0'
                                                            }
                                                        }}
                                                        value={endDate}
                                                        setFormData={handleChangeEndDate}
                                                        options={{
                                                            min_date: startDate
                                                        }}
                                                    />
                                                ) : (
                                                    <FormInput
                                                        compStyle={{
                                                            wrapper: {
                                                                marginBottom: '0'
                                                            }
                                                        }}
                                                        readonly
                                                        value={formatValue(endDate)}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className={
                                                mobileRangeDateCustom && isMobile
                                                    ? 'react-datepicker-range react-datepicker-range-custom-mobile'
                                                    : 'react-datepicker-range'
                                            }
                                        >
                                            {(!isMobile || mobileRangeDateCustom) && (
                                                <DatePicker
                                                    dateFormatCalendar={execludeYear ? 'LLLL' : 'LLLL yyyy'}
                                                    maxDate={maxDate}
                                                    selected={startDate}
                                                    startDate={startDate}
                                                    endDate={endDate}
                                                    onChange={changeHandler}
                                                    selectsRange
                                                    monthsShown={mobileRangeDateCustom && isMobile ? 1 : 2}
                                                    locale={langLocale}
                                                    inline
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={css.bottomContainer}>
                                    {!adminPage ? (
                                        <>
                                            <SingleButton
                                                customButton={{
                                                    butWrapper: classnames(css.modalBtn, css.modalCancelBtn),
                                                    singleButton: css.modalSingleBtn
                                                }}
                                                type='cancel'
                                                clickFunc={() => handleClose()}
                                                buttonText={
                                                    isMobile ? (
                                                        <T textName='reset' defaultStr='Сбросить' page='all' />
                                                    ) : (
                                                        <T textName='cancel' defaultStr='Отмена' page='all' />
                                                    )
                                                }
                                            />
                                            <SingleButton
                                                customButton={{
                                                    butWrapper: css.modalBtn,
                                                    singleButton: css.modalSingleBtn
                                                }}
                                                type='ok'
                                                clickFunc={() => {
                                                    setVal({
                                                        start: formatDate(startDate),
                                                        end: formatDate(endDate)
                                                    });
                                                    handleClose({
                                                        start: formatDate(startDate),
                                                        end: formatDate(endDate)
                                                    });
                                                }}
                                                buttonText={<T textName='apply' defaultStr='Применить' page='all' />}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                className={
                                                    mobileRangeDateCustom && isMobile
                                                        ? classnames(css.buttonCustom, css.buttonCancelCustom, css.hoverButton)
                                                        : isMobile
                                                        ? classnames(css.buttonMobile, css.buttonMobileCancel, css.hoverButton)
                                                        : classnames(css.button, css.buttonCancel, css.hoverButton)
                                                }
                                                onClick={() => {
                                                    handleClose();
                                                }}
                                            >
                                                {isMobile ? (
                                                    <T textName='reset' defaultStr='Сбросить' page='all' />
                                                ) : (
                                                    <T textName='cancel' defaultStr='Отмена' page='all' />
                                                )}
                                            </button>
                                            <button
                                                className={
                                                    mobileRangeDateCustom && isMobile
                                                        ? classnames(css.buttonCustom, css.buttonOkCustom, css.hoverButton)
                                                        : isMobile
                                                        ? classnames(css.buttonMobile, css.buttonMobileOk, css.hoverButton)
                                                        : classnames(css.button, css.buttonOk, css.hoverButton)
                                                }
                                                onClick={() => {
                                                    setVal({
                                                        start: formatDate(startDate),
                                                        end: formatDate(endDate)
                                                    });
                                                    handleClose({
                                                        start: formatDate(startDate),
                                                        end: formatDate(endDate)
                                                    });
                                                }}
                                            >
                                                {isMobile ? (
                                                    <T textName='apply' defaultStr='Применить' page='all' />
                                                ) : (
                                                    <T textName='select' defaultStr='Выбрать' page='all' />
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>,
                document?.body
            )}
        </>
    );
};
CustomDatePicker.propTypes = {
    handleClose: PropTypes.func,
    setVal: PropTypes.func,
    pickerElement: PropTypes.object
};
export default CustomDatePicker;
